"use client";

import Image from "next/image";
import Link from "next/link";
import { BookOpen, CircleUserRound, Compass, PawPrint, Signpost, UsersRound } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useRouter } from "next/navigation";

import { UserAvatarMenu } from "@/components/nav/UserAvatarMenu";
import { navIconProps, navLinkClass } from "@/components/nav/navIcons";
import { Container } from "@/components/ui/Container";
import { StartQuizButton } from "@/components/analytics/StartQuizButton";
import { dynamicWithRetry } from "@/lib/chunks/dynamicWithRetry";
import { useAuthUser } from "@/lib/auth/useAuthUser";

const AuthModal = dynamicWithRetry(
  () => import("@/components/auth/AuthModal").then((m) => m.AuthModal),
  { surface: "auth_marketing_nav" },
);

const homeLink = { href: "/", label: "Home", icon: PawPrint } as const;

// Absolute hash (not "#regions") so it resolves correctly from any page
// that renders MarketingNav — the #regions section only exists on "/".
// next/link handles both cases: cross-route it does a client-side transition
// to "/" and then scrolls to the id; same-route it's a hash-only scroll.
const links = [
  { href: "/#regions", label: "Countries", icon: Signpost },
  { href: "/blog", label: "Safari Blog", icon: BookOpen },
  { href: "/team", label: "Meet the team", icon: UsersRound },
] as const;

type Props = {
  // The value SiteNav's async fetchMe resolved for this render: null =
  // confirmed logged out, undefined = not yet known (the Suspense fallback
  // while fetchMe is still in flight). useAuthUser below reconciles this
  // against sessionStorage before paint — see its doc comment for why the
  // server value alone isn't enough to avoid a Login/Sign up flash for a
  // logged-in visitor on a client-side nav.
  user?: { name: string; email: string } | null;
};

// Occupies the same slot as UserAvatarMenu's h-9 w-9 trigger while auth
// state is still unknown, so nothing shifts when it resolves to the avatar.
// Deliberately blank, not a skeleton shimmer — this should only ever be
// visible for the duration of one fetchMe round trip.
function NavAuthPlaceholder() {
  return <div aria-hidden="true" className="h-9 w-9" />;
}

export function MarketingNav({ user: serverUser }: Props) {
  const user = useAuthUser(serverUser);
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authHasOpened, setAuthHasOpened] = useState(false);
  const panelId = useId();
  const router = useRouter();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function openAuth() {
    setOpen(false);
    setAuthOpen(true);
    setAuthHasOpened(true);
  }

  return (
    <>
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-border-subtle bg-surface/80 backdrop-blur-md">
        <Container className="h-[var(--bsa-nav-h)] flex items-center justify-between">
          <Link
            href="/"
            className="inline-flex items-center text-primary focus-ring rounded-[var(--radius-sm)]"
            aria-label="BestSafari.Africa"
          >
            <Image
              src="/logo.svg"
              alt="BestSafari.Africa"
              width={260}
              height={52}
              className="h-12 w-auto"
              priority
            />
          </Link>

          {/* xl, not md: with Home and the link icons the full row needs
              ~1000px beside the logo and CTA, so anything narrower (tablets,
              small laptops) uses the hamburger layout below instead. */}
          <div className="hidden xl:flex items-center gap-10 text-lg font-medium text-text-muted">
            {[homeLink, ...links].map((l) => (
              <Link key={l.href} href={l.href} className={navLinkClass}>
                <l.icon {...navIconProps} />
                {l.label}
              </Link>
            ))}
            {user === null && (
              <button
                type="button"
                onClick={openAuth}
                className={navLinkClass}
              >
                <CircleUserRound {...navIconProps} />
                Login / Sign up
              </button>
            )}
            {/* Account slot sits before the CTA in every auth state, so the
                CTA doesn't move when a visitor logs in or out (the links to
                its left still shift, since the row is right-aligned). */}
            {user && <UserAvatarMenu user={user} />}
            {user === undefined && <NavAuthPlaceholder />}
            {/* Documented one-off: 48px tall with 18px text and px-8
                matches no size step (xl is 56px). Kept as an override to
                preserve the shipped nav exactly; if this combo spreads, promote
                it to a real step instead of copying this. */}
            <StartQuizButton
              source="nav"
              variant="cta"
              size="sm"
              className="ml-2 h-12 px-8 text-lg!"
            >
              <Compass size={18} strokeWidth={2} aria-hidden="true" />
              Begin Your Adventure
            </StartQuizButton>
          </div>

          <div className="xl:hidden flex items-center gap-2">
            <StartQuizButton source="nav" variant="cta" size="sm">
              Begin Your Adventure
            </StartQuizButton>
            {user && <UserAvatarMenu user={user} />}
            {user === undefined && <NavAuthPlaceholder />}
            <button
              type="button"
              className="inline-flex items-center justify-center h-9 w-10 rounded-[var(--radius-sm)] border border-border bg-surface-muted text-text shadow-[var(--shadow-sm)] transition-colors hover:bg-surface focus-ring"
              aria-controls={panelId}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              <span className="sr-only">Toggle menu</span>
              <svg
                aria-hidden="true"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </Container>

        <div
          id={panelId}
          className={open ? "xl:hidden border-t border-border" : "hidden"}
        >
          <Container className="py-4 flex flex-col gap-2 text-base">
            {[homeLink, ...links].map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="inline-flex items-center gap-2.5 py-2 text-text-muted hover:text-primary transition-colors focus-ring rounded-[var(--radius-sm)]"
                onClick={() => setOpen(false)}
              >
                <l.icon {...navIconProps} />
                {l.label}
              </Link>
            ))}
            {user === null && (
              <button
                type="button"
                onClick={openAuth}
                className="inline-flex items-center gap-2.5 py-2 text-left text-text-muted hover:text-primary transition-colors focus-ring rounded-[var(--radius-sm)]"
              >
                <CircleUserRound {...navIconProps} />
                Login / Sign up
              </button>
            )}
          </Container>
        </div>

        {user === null && authHasOpened && (
          <AuthModal
            open={authOpen}
            onOpenChange={setAuthOpen}
            onSuccess={() => {
              setAuthOpen(false);
              router.refresh();
            }}
            initialTab="login"
            eyebrow={{ login: "Welcome back" }}
            title={{
              login: (
                <>
                  Access your{" "}
                  <span className="font-editorial font-normal italic text-primary">
                    saved matches
                  </span>
                </>
              ),
            }}
            signupSubmitLabel="Create account"
            loginSubmitLabel="Log in"
          />
        )}
      </nav>
      {/* Flow spacer — the nav above is fixed and takes no layout space; this
          reserves its height so page content isn't clipped underneath it. */}
      <div aria-hidden="true" className="h-[var(--bsa-nav-h)] shrink-0" />
    </>
  );
}
